/**
 * ProrationCheckoutModal — guided flow for paying the upgrade proration (iter-168e).
 *
 * Shows when:
 *  - `comp.pending_proration_status === "pending"` AND
 *  - `comp.pending_proration_amount > 0` AND
 *  - `comp.pending_proration_init_point` is available (from /composition/update)
 *
 * Behavior:
 *  - Renders modal with countdown ("Tienes 2d 14h"), amount, and big CTA "Pagar ahora"
 *  - Opens MP init_point in a new window
 *  - Polls /billing/me every 5s to detect when the payment is confirmed
 *  - On confirm: closes modal + toast success
 *  - On expire: closes modal + warns user about rollback
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Clock, ExternalLink, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";

function _countdown(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return { expired: true, label: "expirado" };
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    if (days >= 1) return { expired: false, label: `${days}d ${hours}h` };
    return { expired: false, label: `${hours}h ${minutes}m` };
  } catch { return null; }
}

function _fmtMoney(amount, currency) {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat("es-CO", {
      style: "currency", currency: currency || "COP", maximumFractionDigits: 0,
    }).format(amount);
  } catch { return `${currency} ${amount}`; }
}

export function ProrationCheckoutModal({ comp, lang = "es", initPoint, onClose }) {
  const isEs = lang !== "en";
  const qc = useQueryClient();
  const [tick, setTick] = useState(0);

  // Force re-render every 30s for the countdown
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // Track tick value so eslint doesn't complain about it being unused (it
  // intentionally triggers re-renders via setTick).
  void tick;

  const cd = _countdown(comp?.pending_proration_expires_at);
  const open = comp?.pending_proration_status === "pending" && Number(comp?.pending_proration_amount || 0) > 0;

  const openMp = useMutation({
    mutationFn: async () => {
      if (initPoint) {
        // Open in same-tab popup window. window.open returns null if blocked.
        const w = window.open(initPoint, "_blank", "noopener,noreferrer,width=520,height=700");
        if (!w) {
          throw new Error("popup_blocked");
        }
        return w;
      }
      throw new Error("missing_init_point");
    },
    onSuccess: () => {
      // Start polling for completion (5s interval)
      const poll = setInterval(() => {
        qc.invalidateQueries({ queryKey: ["billing-me"] });
      }, 5000);
      // Stop polling after 5 min OR when modal closes
      setTimeout(() => clearInterval(poll), 5 * 60_000);
    },
    onError: (e) => {
      if (e?.message === "popup_blocked") {
        toast.error(
          isEs ? "El popup fue bloqueado. Habilítalo y reintenta." : "Popup blocked. Allow popups and retry.",
        );
      } else {
        toast.error(isEs ? "No pudimos abrir el checkout." : "Could not open checkout.");
      }
    },
  });

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="proration-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-900">
            <Clock className="h-5 w-5 text-amber-600" />
            {isEs ? "Confirma tu upgrade" : "Confirm your upgrade"}
          </DialogTitle>
          <DialogDescription className="pt-2 text-slate-600">
            {isEs
              ? "Activamos tu nuevo plan al instante. Para que se mantenga, completa el pago del ajuste prorrateado dentro del plazo."
              : "We unlocked your new plan instantly. To keep it, finish the prorated charge within the window."}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-amber-900">
              {isEs ? "A pagar" : "Due now"}
            </span>
            <span data-testid="proration-amount" className="text-2xl font-semibold text-amber-900 tabular-nums">
              {_fmtMoney(comp.pending_proration_amount, comp.currency)}
            </span>
          </div>
          <div className="flex items-baseline justify-between mt-2 text-xs">
            <span className="text-amber-700">
              {isEs ? "Tiempo restante" : "Time remaining"}
            </span>
            <span data-testid="proration-countdown" className="font-medium text-amber-900 tabular-nums">
              {cd?.expired ? (isEs ? "Expirado" : "Expired") : (cd?.label || "—")}
            </span>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <Button
            data-testid="proration-pay-btn"
            onClick={() => openMp.mutate()}
            disabled={openMp.isPending || cd?.expired || !initPoint}
            size="lg"
            className="gap-2 bg-amber-600 hover:bg-amber-700 h-12 text-base font-semibold rounded-xl"
          >
            <ExternalLink className="h-4 w-4" />
            {isEs ? "Pagar ahora en MercadoPago" : "Pay now on MercadoPago"}
          </Button>
          <Button
            data-testid="proration-dismiss-btn"
            variant="ghost"
            onClick={() => onClose?.()}
            className="gap-1.5 text-slate-500 h-11"
          >
            <X className="h-3.5 w-3.5" />
            {isEs ? "Lo pago después" : "Pay later"}
          </Button>
        </div>

        {cd?.expired ? (
          <p className="mt-3 text-xs text-rose-600 text-center">
            {isEs
              ? "Tu plan ya volvió al anterior porque expiró el plazo de pago."
              : "Your plan was reverted because the payment window expired."}
          </p>
        ) : (
          <p className="mt-3 text-[11px] text-slate-400 text-center">
            {isEs
              ? "Si no completas el pago, volveremos automáticamente a tu plan anterior."
              : "If you don't complete the payment, we'll automatically restore your previous plan."}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
